import { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../../../../config/api";
import { motion } from "framer-motion";
import {
  Receipt, Calendar, AlertCircle, Shield,
} from "lucide-react";
import RegularLoader from "../../../../Component/Loader/RegularLoader";
import { useNavigate } from "react-router-dom";

// ─── HELPERS ─────────────────────────────────────────────────────────────
const fmt = (n) =>
  Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

function daysRemaining(createdDate, validityDays) {
  if (!createdDate || !validityDays) return null;
  const start = new Date(createdDate);
  const end = new Date(start.getTime() + validityDays * 86400000);
  const diff = Math.ceil((end - new Date()) / 86400000);
  return Math.max(diff, 0);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso)
    .toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    })
    .toUpperCase();
}

function expiryDate(createdDate, validityDays) {
  if (!createdDate || !validityDays) return "—";
  const start = new Date(createdDate);
  const end = new Date(start.getTime() + validityDays * 86400000);
  return fmtDate(end.toISOString());
}

// ─── STATUS DOT ──────────────────────────────────────────────────────────
function StatusDot({ active }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`w-2 h-2 rounded-full ${
          active ? "bg-emerald-400" : "bg-slate-400"
        }`}
        style={active ? { boxShadow: "0 0 6px #34D399" } : {}}
      />
      <span
        className="text-[11px] font-black uppercase tracking-widest"
        style={{ color: active ? "#10B981" : "#94A3B8" }}
      >
        {active ? "Active" : "Inactive"}
      </span>
    </div>
  );
}

// ─── METRIC TILE ─────────────────────────────────────────────────────────
function MetricTile({ label, value, color, isLarge }) {
  return (
    <div>
      <p className="text-[11px]" style={{ color: "rgba(186,210,255,0.65)" }}>{label}</p>
      <p
        className="font-black"
        style={{
          fontSize: isLarge ? 30 : 24,
          color,
        }}
      >
        {value}
      </p>
    </div>
  );
}

const getIsRetailerMobile = () => {
  if (typeof window === "undefined") return false;

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

  return (
    window.innerWidth < 640 &&
    (authUser?.role === 5 || authUser?.role === 6)
  );
};

// ─── CARD ────────────────────────────────────────────────────────────────
function SubscribedCard({ sub, index }) {

  const navigate = useNavigate();
  
  const pkg = sub.package || {};

  const isActive =
    sub.status === 1 &&
    daysRemaining(sub.invoice_created_date, sub.validity_days) > 0;

  const daysLeft = daysRemaining(
    sub.start_date,
    sub.validity_days
  );

  const Deviceenrolled = Math.max(sub.enroll_max || 0 ) - (sub.balance || 0)

  const devicesLeft = Math.max(
    (pkg.enroll_max || 0) - (pkg.sold_count || 0),
    0
  );

  const planName = pkg.name || "Extended Warranty";
  const planType = pkg.product_type || "Extended Warranty";
  
  const priceLine = `₹${fmt(pkg.mrp || 1999)} | ${
    pkg.enroll_max || 0
  } devices max`;

  const expiry = expiryDate(
    sub.invoice_created_date,
    sub.validity_days
  );

  // Lighter tints — readable on the dark right panel
  const daysColor =
    daysLeft <= 5
      ? "#FCA5A5"   // red-300
      : daysLeft <= 10
      ? "#FCD34D"   // amber-300
      : "#BAE6FD";  // sky-200
  
  const isRetailerMobile = getIsRetailerMobile();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <div
        className={`rounded-[26px] overflow-hidden flex flex-col sm:flex-row ${
          isRetailerMobile
            ? "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
            : ""
        }`}
        style={{
          background: isRetailerMobile ? "transparent" : "#FFFFFF",

          border: isRetailerMobile
            ? "1px solid rgba(255,255,255,0.3)"   // glass border
            : isActive
            ? "1px solid #BAE6FD"
            : "1px solid #E2E8F0",

          boxShadow: isRetailerMobile
            ? "0 20px 50px rgba(31,38,135,0.25)"
            : isActive
            ? "0 6px 24px rgba(14,165,233,0.13)"
            : "0 4px 20px rgba(15,23,42,0.06)",
        }}
      >
        {/* LEFT */}
        <div className="flex-1 p-6 border-b sm:border-b-0 sm:border-r" style={{ borderColor: "#F1F5F9" }}>
          <div className="flex gap-3 mb-4">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}
            >
              <Shield size={16} color="#2563EB" />
            </div>

            <div>
              <h3 className="font-bold text-sm" style={{ color: "#0F172A" }}>
                {planName}
              </h3>
              <h4 className="font-semibold text-xs" style={{ color: "#0F172A" }}>
                12 Month {planType}
              </h4>
              <p className="text-xs" style={{ color: "#64748B" }}>{priceLine}</p>
            </div>
          </div>

          {/* INFO BOX */}
          <div
            className={`p-4 rounded-xl mb-4 ${
              isRetailerMobile
                ? "bg-white/10 backdrop-blur-md border border-white/20"
                : ""
            }`}
            style={{
              background: isRetailerMobile ? "rgba(255,255,255,0.05)" : "#F8FAFC",
              border: isRetailerMobile
                ? "1px solid rgba(255,255,255,0.2)"
                : "1px solid #E2E8F0",
            }}
          >
            <div className="flex justify-between text-sm mb-2">
              <span className="font-semibold" style={{ color: "#1E293B" }}>Duration</span>
              <span className="font-semibold" style={{ color: "#1E293B" }}>
                {sub.validity_days} DAYS
              </span>
            </div>

            <div className="flex justify-between text-sm mb-2">
              <span className="font-semibold" style={{ color: "#1E293B" }}>Expiry Date</span>
              <span className="font-semibold" style={{ color: "#1E293B" }}>
                {expiry}
              </span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="font-semibold" style={{ color: "#1E293B" }}>Payment Mode</span>
              <span className="font-semibold" style={{ color: "#2563EB" }}>
                Cash
              </span>
            </div>
          </div>

          <p className="text-xs mb-4 flex items-center gap-1" style={{ color: "#1E293B" }}>
            <Calendar size={12} />
            <span className="font-semibold" style={{ color: "#1E293B" }}>Activated on {fmtDate(sub.invoice_created_date)}</span>
          </p>

          <button
              className="px-4 py-2 rounded-xl text-sm"
              style={{
                border: "1px solid #E2E8F0",
                color: "#475569",
                background: isRetailerMobile ? "rgba(255,255,255,0.05)" : "#F8FAFC",
              }}
              onClick={() =>
                navigate("/retwarnty", {
                  state: {
                    retailer_id: sub.retailer_id,
                    subscription_id: sub.id, // ✅ IMPORTANT
                    fromSubscription: true,  // optional flag
                  },
                })
              }
            >
              <Receipt size={14} className="inline mr-1" />
              View Enrolled
            </button>
        </div>

        {/* RIGHT — deeper navy, richer gradient */}
        <div
          className="p-6 flex flex-col justify-between w-full sm:w-auto rounded-b-2xl sm:rounded-b-none sm:rounded-r-2xl"
          style={{
            background:
              "linear-gradient(160deg, #0C1A3A 0%, #1641A0 55%, #2563EB 100%)",
          }}
        >
          <StatusDot active={isActive} />

          <div className="mt-4 grid grid-cols-3 sm:block gap-4 sm:space-y-4">
            <MetricTile
              label="Days remaining"
              value={daysLeft}
              color={daysColor}
              isLarge
            />

            <MetricTile
              label="Device Enrolled"
              value={Deviceenrolled || 0}
              color="#BAE6FD"
            />

            <MetricTile
              label="Enrollment Left"
              value={sub.balance || 0}
              color="#BAE6FD"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────
export default function SubscribedTab({ companyId }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return;

    axios
      .get(`${BASE_URL}warranty/subscriptions`, {
        params: { retailer_id: companyId },
      })
      .then((res) => setSubs(res.data?.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [companyId]);

  if (loading) {
    return (
      <RegularLoader /> 
    );
  }

  if (!subs.length) {
    return (
      <div className="text-center py-10 text-slate-400">
        No subscriptions found
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {subs.map((sub, i) => (
        <SubscribedCard key={sub.id} sub={sub} index={i} />
      ))}
    </div>
  );
}


// import { useState, useEffect } from "react";
// import axios from "axios";
// import { BASE_URL } from "../../../../config/api";
// import { motion } from "framer-motion";
// import {
//   Receipt, Ban, ArrowUpCircle, RefreshCw,
//   Calendar, Clock, Smartphone, CheckCircle2,
//   AlertCircle, ChevronRight, Shield,
// } from "lucide-react";

// // ─── HELPERS ──────────────────────────────────────────────────────────────────
// const fmt = (n) => Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// function daysRemaining(createdDate, validityDays) {
//   if (!createdDate || !validityDays) return null;
//   const start  = new Date(createdDate);
//   const end    = new Date(start.getTime() + validityDays * 24 * 60 * 60 * 1000);
//   const now    = new Date();
//   const diff   = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
//   return Math.max(diff, 0);
// }

// function fmtDate(iso) {
//   if (!iso) return "—";
//   return new Date(iso).toLocaleDateString("en-IN", {
//     day: "2-digit", month: "long", year: "numeric",
//   }).toUpperCase();
// }

// function expiryDate(createdDate, validityDays) {
//   if (!createdDate || !validityDays) return "—";
//   const start = new Date(createdDate);
//   const end   = new Date(start.getTime() + validityDays * 24 * 60 * 60 * 1000);
//   return fmtDate(end.toISOString());
// }

// // ─── ATOM: StatusDot ──────────────────────────────────────────────────────────
// function StatusDot({ active }) {
//   return (
//     <div className="flex items-center gap-1.5">
//       <span
//         className={`w-2 h-2 rounded-full ${active ? "bg-emerald-400" : "bg-slate-400"}`}
//         style={active ? { boxShadow: "0 0 6px #34D399" } : {}}
//       />
//       <span
//         className="text-[11px] font-black uppercase tracking-widest"
//         style={{ color: active ? "#059669" : "#64748B" }}
//       >
//         {active ? "Active" : "Inactive"}
//       </span>
//     </div>
//   );
// }

// // ─── ATOM: MetricTile ─────────────────────────────────────────────────────────
// function MetricTile({ label, value, color = "#1E1B4B", isLarge }) {
//   return (
//     <div className="flex flex-col">
//       <span
//         className="font-body"
//         style={{ fontSize: 11, color: "#64748B", fontWeight: 500 }}
//       >
//         {label}
//       </span>
//       <span
//         className="font-display font-black leading-none mt-0.5"
//         style={{ fontSize: isLarge ? 32 : 26, color }}
//       >
//         {value}
//       </span>
//     </div>
//   );
// }

// // ─── ATOM: AutoRenewToggle ─────────────────────────────────────────────────────
// function AutoRenewToggle({ on }) {
//   const [isOn, setIsOn] = useState(on);
//   return (
//     <button
//       onClick={() => setIsOn(!isOn)}
//       className="flex items-center gap-1 px-3 py-1 rounded-full transition-all duration-200 font-black text-[10px] uppercase tracking-widest"
//       style={{
//         background:  isOn ? "#22C55E" : "#E2E8F0",
//         color:       isOn ? "#fff"    : "#64748B",
//         minWidth:    52,
//         justifyContent: "center",
//         boxShadow:   isOn ? "0 2px 8px rgba(34,197,94,0.4)" : "none",
//       }}
//     >
//       {isOn ? "ON" : "OFF"}
//     </button>
//   );
// }

// // ─── ATOM: InfoRow ────────────────────────────────────────────────────────────
// function InfoRow({ label, children }) {
//   return (
//     <div className="flex items-center justify-between py-1">
//       <span style={{ fontSize: 12, color: "#64748B", fontFamily: "var(--font-body)" }}>
//         {label}
//       </span>
//       <div>{children}</div>
//     </div>
//   );
// }

// // ─── MOLECULE: SubscribedCard ─────────────────────────────────────────────────
// function SubscribedCard({ sub, index }) {
//   const pkg        = sub.package || {};
//   const pmt        = (() => { try { return JSON.parse(sub.payment_json); } catch { return {}; } })();
//   const inv        = (() => { try { return JSON.parse(sub.invoice_json); } catch { return {}; } })();

//   const isActive   = sub.status === 1 && daysRemaining(sub.invoice_created_date, sub.validity_days) > 0;
//   const daysLeft   = daysRemaining(sub.invoice_created_date, sub.validity_days);
//   const devicesLeft = Math.max((pkg.enroll_max || sub.enroll_max || 0) - (pkg.sold_count || 0), 0);
//   const purchased  = pmt?.amount   ? `₹${fmt(pmt.amount)}` : sub.amount ? `₹${fmt(sub.amount)}` : "₹1,999";
//   const planName   = pkg.product_type || pkg.name || "Extended Warranty";
//   const planSubtitle = pkg.name || "";
//   const mrp        = pkg.mrp || 1999;
//   const priceLine  = `₹${fmt(mrp)} | ${pkg.enroll_max || sub.enroll_max || 0} devices max`;
//   const activatedOn = sub.invoice_created_date || sub.created_at;
//   const expiry     = expiryDate(activatedOn, sub.validity_days);
//   const invoiceNum = inv?.invoice_number || sub.zoho_invoice_id || "—";
//   const payMode    = pmt?.payment_mode || "—";

//   const daysColor  = daysLeft <= 5 ? "#EF4444" : daysLeft <= 10 ? "#F59E0B" : "#1E1B4B";

//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 20 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.4, delay: index * 0.08 }}
//       className="w-full"
//       style={{ fontFamily: "var(--font-body)" }}
//     >
//       <div
//         className="rounded-[20px] overflow-hidden flex flex-col sm:flex-row"
//         style={{
//           background:  "#FFFFFF",
//           boxShadow: "0 4px 20px rgba(37,99,235,0.08)",
//           border: "1px solid #E0E7FF",
//         }}
//       >
//         {/* ── LEFT PANEL ── */}
//         <div className="flex-1 p-5 sm:p-6 border-b sm:border-b-0 sm:border-r"
//           style={{ borderColor: "#F1F5F9" }}>

//           {/* Plan name + subtitle */}
//           <div className="mb-4">
//             <div className="flex items-start gap-2 mb-1">
//               <div
//                 className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
//                 style={{ background: "#EFF6FF" }}
//               >
//                 <Shield size={15} color="#2563EB" />
//               </div>
//               <div className="min-w-0">
//                 <h3
//                   className="font-display font-black leading-tight"
//                   style={{ fontSize: 16, color: "#1E1B4B" }}
//                 >
//                   {planName}
//                 </h3>
//                 <p style={{ fontSize: 11, color: "#64748B", marginTop: 2, lineHeight: 1.4 }}>
//                   {priceLine}
//                 </p>
//               </div>
//             </div>
//           </div>

//           {/* Info rows */}
//           <div
//             className="rounded-xl p-4 mb-4"
//             style={{ background: "#F8F8FF", border: "1px solid #EDE9FE" }}
//           >
//             <InfoRow label="Duration">
//               <span
//                 className="font-display font-black"
//                 style={{ fontSize: 13, color: "#1E1B4B" }}
//               >
//                 {sub.validity_days} DAYS
//               </span>
//             </InfoRow>

//             {/* <InfoRow label="Auto Renew">
//               <AutoRenewToggle on={false} />
//             </InfoRow> */}

//             <InfoRow label="Expiry Date">
//               <span
//                 className="font-display font-black"
//                 style={{ fontSize: 12, color: daysLeft <= 5 ? "#EF4444" : "#1E1B4B" }}
//               >
//                 {expiry}
//               </span>
//             </InfoRow>

//             <InfoRow label="Payment Mode">
//               <span style={{ fontSize: 12, fontWeight: 600, color: "#1E1B4B" }}>
//                 {payMode}
//               </span>
//             </InfoRow>
//           </div>

//           {/* Activated on */}
//           <div
//             className="flex items-center gap-1.5 mb-5"
//             style={{ fontSize: 11, color: "#94A3B8" }}
//           >
//             <Calendar size={11} />
//             <span>Activated on {fmtDate(activatedOn)}</span>
//           </div>

//           {/* Action buttons */}
//           <div className="flex gap-2">
//             <button
//               className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold transition-all hover:opacity-80"
//               style={{
//                 background: "#F1F5F9",
//                 color:      "#475569",
//                 border:     "1px solid #E2E8F0",
//               }}
//             >
//               <Receipt size={13} /> View Receipt
//             </button>
//             {/* <button
//               className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold transition-all hover:opacity-80"
//               style={{
//                 background: "#FEF2F2",
//                 color:      "#EF4444",
//                 border:     "1px solid #FECACA",
//               }}
//             >
//               <Ban size={13} /> Deactivate Plan
//             </button> */}
//           </div>
//         </div>

//         {/* ── RIGHT PANEL ── */}
//         <div
//           className="flex flex-col justify-between sm:w-48 p-5 sm:p-6"
//           style={{ background: "#FAFAFA", minWidth: 160 }}
//         >
//           {/* Status */}
//           <StatusDot active={isActive} />

//           {/* Metrics */}
//           <div className="flex flex-col gap-5 mt-4 flex-1 justify-center">
//             <MetricTile
//               label="Days remaining"
//               value={daysLeft ?? "—"}
//               color={daysColor}
//               isLarge
//             />
//             <MetricTile
//               label="Purchased"
//               value={pkg.sold_count ?? 0}
//               color="#1E1B4B"
//             />
//             <MetricTile
//               label="Devices left"
//               value={devicesLeft}
//               color="#1E1B4B"
//             />
//           </div>

//           {/* Upgrade button */}
//           {/* <button
//             className="w-full py-3 rounded-2xl font-black text-white text-[13px] uppercase tracking-widest
//               transition-all active:scale-95 hover:opacity-90 mt-5"
//             style={{
//               background:  "linear-gradient(135deg,#EF4444,#F97316)",
//               boxShadow:   "0 4px 16px rgba(239,68,68,0.4)",
//               letterSpacing: "0.06em",
//             }}
//           >
//             UPGRADE
//           </button> */}
//         </div>
//       </div>
//     </motion.div>
//   );
// }

// // ─── ORGANISM: SubscribedTab ──────────────────────────────────────────────────
// export default function SubscribedTab({ companyId }) {
//   const [subs,    setSubs]    = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error,   setError]   = useState(null);

//   useEffect(() => {
//     if (!companyId) return;
//     setLoading(true);
//     axios
//       .get(`${BASE_URL}warranty/subscriptions`, {
//         params: { retailer_id: companyId },
//       })
//       .then((res) => {
//         setSubs(res.data?.data?.data || []);
//       }) 
//       .catch((e) => {
//         console.error("Subscriptions fetch error", e);
//         setError("Failed to load subscriptions.");
//       })
//       .finally(() => setLoading(false));
//   }, [companyId]);

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center py-16">
//         <div className="flex flex-col items-center gap-3 text-slate-400">
//           <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
//           <span className="text-sm font-medium">Loading subscriptions…</span>
//         </div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex items-center justify-center py-16">
//         <div className="flex items-center gap-2 text-red-500">
//           <AlertCircle size={16} />
//           <span className="text-sm font-semibold">{error}</span>
//         </div>
//       </div>
//     );
//   }

//   if (!subs.length) {
//     return (
//       <div className="flex flex-col items-center justify-center py-20 text-slate-400">
//         <Shield size={40} strokeWidth={1.2} className="mb-3 opacity-30" />
//         <p className="text-sm font-semibold">No subscriptions found</p>
//         <p className="text-xs mt-1">Your purchased plans will appear here</p>
//       </div>
//     );
//   }

//   return (
//     <div className="grid grid-cols-2 gap-4">
//       {subs.map((sub, i) => (
//         <SubscribedCard key={sub.id} sub={sub} index={i} />
//       ))}
//     </div>
//   );
// }